import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchPlanungsLage } from '../../services/planungsphaseService';
import type { Planungsfrage, Planungshinweis, Planungsphase } from './types';

/**
 * Laedt die Lage der Planungsphase eines Projekts.
 *
 * Legt bewusst NICHTS an: das blosse Oeffnen der Kachel darf keinen Datensatz
 * erzeugen. Das ist dieselbe Ueberlegung wie beim Schreibschutz fuer
 * Nur-Ansicht-Projekte, wo Selbstheilungs-Migrationen beim reinen Lesen
 * geschrieben hatten. Angelegt wird erst, wenn der Anwender die Grundlagen
 * speichert.
 */
export const usePlanung = (projectId: string | null) => {
    const [phase, setPhase] = useState<Planungsphase | null>(null);
    const [fragen, setFragen] = useState<Planungsfrage[]>([]);
    const [hinweise, setHinweise] = useState<Planungshinweis[]>([]);
    const [laden, setLaden] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);

    // Lauf-Zaehler gegen spaete Antworten: wechselt der Anwender das Projekt,
    // waehrend die erste Abfrage noch laeuft, darf deren Ergebnis die
    // inzwischen geladene zweite Lage nicht ueberschreiben.
    const lauf = useRef(0);

    const neuLaden = useCallback(async () => {
        const meiner = ++lauf.current;
        if (!projectId || projectId === 'temp-id') {
            setPhase(null); setFragen([]); setHinweise([]); setLaden(false); setFehler(null);
            return;
        }
        setLaden(true);
        setFehler(null);
        try {
            const lage = await fetchPlanungsLage(projectId);
            if (meiner !== lauf.current) return;
            setPhase(lage.phase);
            setFragen(lage.fragen);
            setHinweise(lage.hinweise);
        } catch (e) {
            if (meiner !== lauf.current) return;
            setFehler(e instanceof Error ? e.message : String(e));
        } finally {
            if (meiner === lauf.current) setLaden(false);
        }
    }, [projectId]);

    useEffect(() => { void neuLaden(); }, [neuLaden]);

    /** Eine geaenderte Frage in die Liste zurueckspielen, ohne alles neu zu
     *  laden — sonst springt die Liste bei jedem Tastendruck. */
    const uebernehmeFrage = useCallback((frage: Planungsfrage) => {
        setFragen(alt => alt.map(f => (f.id === frage.id ? frage : f)));
    }, []);

    const entferneFrage = useCallback((id: string) => {
        setFragen(alt => alt.filter(f => f.id !== id));
    }, []);

    const ergaenzeFragen = useCallback((neue: Planungsfrage[]) => {
        if (!neue || !neue.length) return;
        setFragen(alt => {
            const bekannt = new Set(alt.map(f => f.id));
            return [...alt, ...neue.filter(f => !bekannt.has(f.id))];
        });
    }, []);

    const uebernehmeHinweis = useCallback((h: Planungshinweis) => {
        setHinweise(alt => (alt.some(x => x.id === h.id)
            ? alt.map(x => (x.id === h.id ? h : x))
            : [...alt, h].sort((a, b) => a.nummer - b.nummer)));
    }, []);

    const entferneHinweis = useCallback((id: string) => {
        setHinweise(alt => alt.filter(h => h.id !== id));
    }, []);

    return {
        phase, setPhase,
        fragen, setFragen,
        hinweise,
        laden, fehler,
        neuLaden,
        uebernehmeFrage, entferneFrage, ergaenzeFragen,
        uebernehmeHinweis, entferneHinweis,
    };
};
